import { FC, useRef, useEffect, useState } from 'react';
import { Icon } from 'common-components';
import { Autocomplete, TextField, Chip } from '../../uiLibraryImports';
import { MultiSelectSearchProps } from './multiSelectSearchTypes';
import globalColors from '../globalColors';
import Theme from '../theme';
import Tooltip from '../../tooltip/tooltip';
import { useTranslationContext } from '../../contexts/translationContext';
import Scroller from '../scroller/scroller';

const MultiSelectSearch: FC<MultiSelectSearchProps> = (props: MultiSelectSearchProps) => {
    const { options, selectedOptions = [], onChange, isPopperOverlay, scrollerHeight = 14 } = props;
    const { translate } = useTranslationContext();
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const [oneLineHeight, setOneLineHeight] = useState<number | null>(null);
    const [limitTags, setLimitTags] = useState<number | null>(null);
    const [resetLimitTags, setResetLimitTags] = useState<boolean>(false);
    const [searchVal, setSearchVal] = useState<string>('');

    const nameToDisplayName = {};
    options.forEach(option => {
        nameToDisplayName[option.name] = option.displayName;
    });

    const onItemChange = (_event: any, newValue: string) => {
        const selectedOptionsCpy = [...selectedOptions];
        if (selectedOptionsCpy.includes(newValue)) {
            selectedOptionsCpy.splice(selectedOptionsCpy.indexOf(newValue), 1);
        } else {
            selectedOptionsCpy.push(newValue);
        }
        onChange?.(_event, selectedOptionsCpy);
    };

    useEffect(() => {
        if (!ref.current) return;
        const resizeObserver = new ResizeObserver(() => {
            const displayedOptions = limitTags !== null ? limitTags : selectedOptions.length;
            if (displayedOptions <= 1 && ref.current.clientHeight !== oneLineHeight && ref.current.clientHeight > 0) {
                setOneLineHeight(ref.current.clientHeight);
                return;
            }

            if (resetLimitTags) {
                setResetLimitTags(false);
                setLimitTags(null);
                return;
            }

            if (oneLineHeight && ref.current.clientHeight > oneLineHeight) {
                const newLimit = Math.max(0, displayedOptions - 1);
                setLimitTags(newLimit);
            } else if (oneLineHeight === null && selectedOptions.length > 1) {
                setResetLimitTags(true);
                setLimitTags(1);
            } else if (limitTags !== null && selectedOptions.length <= limitTags) {
                setLimitTags(null);
            }
        });
        resizeObserver.observe(ref.current);
        // eslint-disable-next-line consistent-return
        return () => resizeObserver.disconnect();
    }, [oneLineHeight, limitTags, selectedOptions.length, resetLimitTags]);

    const onInputChange = (_event: any, value: string, reason: string) => {
        if (reason === 'input') {
            setSearchVal(value.toLowerCase());
        }
    };

    return (
        <Theme>
            <Autocomplete
                multiple
                open={true}
                options={options.map(option => option.name)}
                value={selectedOptions}
                inputValue={searchVal}
                onInputChange={onInputChange}
                onChange={onChange}
                disableCloseOnSelect
                getOptionLabel={option => nameToDisplayName[option] || ''}
                renderInput={params => {
                    return (
                        <TextField
                            variant="standard"
                            ref={ref}
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <div
                                            style={{
                                                borderLeft: '1px solid',
                                                height: '1.5rem',
                                                position: 'absolute',
                                                right: '2.2rem',
                                                opacity: '0.5'
                                            }}
                                        ></div>
                                        <Icon
                                            name={'trashAdmin'}
                                            onClick={() => {
                                                setSearchVal('');
                                                onChange?.(undefined, []);
                                            }}
                                            style={{ position: 'absolute', right: '0.5rem', top: '0.7rem' }}
                                        />
                                    </>
                                ),
                                disableUnderline: true
                            }}
                            placeholder={selectedOptions.length === 0 ? translate('Search') : ''}
                            sx={{
                                '& .MuiAutocomplete-inputRoot': {
                                    backgroundColor: 'white',
                                    paddingRight: '2.5rem !important'
                                },
                                '& .MuiInputBase-root': {
                                    paddingBottom: '0.5rem',
                                    paddingLeft: '1rem',
                                    paddingTop: '0.5rem',
                                    color: globalColors.texts.strong
                                }
                            }}
                        />
                    );
                }}
                renderTags={(value, getTagProps) => {
                    const list = limitTags !== null ? value.slice(0, limitTags) : value;

                    const chips = list.map((option, index) => (
                        <Chip
                            {...getTagProps({
                                index
                            })}
                            className="MuiChip-root"
                            key={option}
                            label={nameToDisplayName[option] || ''}
                            deleteIcon={<></>}
                        />
                    ));

                    if (limitTags !== null && value.length > limitTags) {
                        const remaining = value.slice(list.length);

                        chips.push(
                            <Tooltip
                                key="overflow-tooltip"
                                title={remaining.map(option => nameToDisplayName[option] || '').join(', ')}
                                placement="top"
                            >
                                <span>+{remaining.length}</span>
                            </Tooltip>
                        );
                    }
                    return chips;
                }}
                PopperComponent={() => (
                    <Scroller
                        items={options.filter(option => option.displayName.toLowerCase().includes(searchVal))}
                        height={scrollerHeight}
                        onChange={onItemChange}
                        selectedItems={selectedOptions}
                        isOverlay={isPopperOverlay}
                    />
                )}
                sx={{
                    '.MuiOutlinedInput-notchedOutline': { border: 0 },
                    '& .MuiChip-root': {
                        borderRadius: '0.3rem',
                        color: globalColors.texts.strong,
                        backgroundColor: globalColors.backgrounds.lightPurple,
                        marginRight: '0.5rem',
                        height: '1.5rem',
                        maxWidth: '60%'
                    },
                    '& .MuiChip-label': {
                        padding: '0rem 0.5rem'
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                        display: 'none'
                    }
                }}
            />
        </Theme>
    );
};

export default MultiSelectSearch;
